import cn from 'classnames';
import { Icon, IconNames, Stack, Text } from '@pairy/pds-react';
import { MouseEvent, useEffect, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';

import MobilePdfDownloadButton from '../../@common/Layer/DefaultViewerLayer/PdfDownloadButton/Mobile';
import MobileShareButton from '../../@common/Layer/DefaultViewerLayer/ShareButton/Mobile';
import MobileLanguageButton from '../../@common/Layer/DefaultViewerLayer/LanguageButton/Mobile';
import { isDisableHelpersAtom } from '../../../store/index.atom';
import {
  v2IsThumbnailPanelOpenAtom,
  v2ZoomAtom,
  v2ZoomFitModeAtom,
} from '../../../store/v2/viewer.atom';
import { V2DocumentType } from '../index.types';
import { getNextZoomStep, V2_ZOOM_MAX, V2_ZOOM_MIN } from '../zoom.constants';

import * as Styled from './index.styles';

/* 하단 썸네일 스트립 높이(ThumbnailPanel 모바일 분기와 동일) */
const THUMBNAIL_STRIP_HEIGHT = 148;

interface Props {
  document: V2DocumentType;
  isBulk: boolean;
  domain?: string | null;
  /** 관리자 미리보기 모드(버전 전환 버튼 숨김) */
  isPreview?: boolean;
  ctaFormPage?: number;
  isRedirectCtaFormAppropriate?: boolean;
  isScrollModeEnabled: boolean;
  currentPage: number;
  goToPrevPage: () => void;
  goToNextPage: () => void;
  isNextPageDisabled: boolean;
  isFullscreenActive: boolean;
  toggleFullscreen: () => void;
  translateKoToEn: (item: string) => string;
  isMobileLandscape: boolean;
}

/**
 * V2 모바일 플로팅 컨트롤. V1과 동일하게 배경 바 없이
 * 하단 중앙 반투명 네비 필([◀][목록·페이지][▶])과
 * 접이식 드로어(다운로드/공유/줌/언어/전체화면/V1 전환)로 배치한다.
 * - 세로: 드로어가 우하단에서 위로, 가로: 우상단에서 옆으로 확장(V1 파리티)
 * - 화면 탭으로 토글되는 isDisableHelpers 상태에 따라 숨김 처리
 * - 썸네일 스트립이 열리면 하단 컨트롤이 스트립 위로 올라간다
 */
const MobileBottomBar = ({
  document,
  isBulk,
  domain,
  ctaFormPage,
  isRedirectCtaFormAppropriate,
  currentPage,
  goToPrevPage,
  goToNextPage,
  isNextPageDisabled,
  isFullscreenActive,
  toggleFullscreen,
  translateKoToEn,
  isMobileLandscape,
}: Props) => {
  const [isThumbnailPanelOpen, setIsThumbnailPanelOpen] = useAtom(
    v2IsThumbnailPanelOpenAtom,
  );
  const [isDisableHelpers, setIsDisableHelpers] = useAtom(isDisableHelpersAtom);
  const [zoom, setZoom] = useAtom(v2ZoomAtom);
  const setFitMode = useSetAtom(v2ZoomFitModeAtom);
  // const { setVersion } = useViewerVersion();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  /* 헬퍼 숨김 시 드로어도 닫는다(V1 파리티) */
  useEffect(() => {
    if (isDisableHelpers) {
      setIsDrawerOpen(false);
    }
  }, [isDisableHelpers]);

  const dimmedClassName = cn({ disabled: isDisableHelpers });

  const bottomOffset = 16 + (isThumbnailPanelOpen ? THUMBNAIL_STRIP_HEIGHT : 0);

  /* 숨겨진 상태의 첫 탭은 기능 실행 없이 컨트롤을 다시 켠다(V1 파리티) */
  const handleGuardClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDisableHelpers) {
      return;
    }
    e.stopPropagation();
    setIsDisableHelpers(false);
  };

  /* 컨트롤 영역 탭이 뷰어의 헬퍼 토글로 전파되지 않게 한다 */
  const stopClickPropagation = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const handleClickZoomIn = () => {
    if (zoom >= V2_ZOOM_MAX) {
      return;
    }
    setZoom(getNextZoomStep(zoom, 'in'));
    setFitMode('custom');
  };

  const handleClickZoomOut = () => {
    if (zoom <= V2_ZOOM_MIN) {
      return;
    }
    setZoom(getNextZoomStep(zoom, 'out'));
    setFitMode('custom');
  };

  /* V1 파리티: 세로는 위/아래 캐럿, 가로는 좌/우 캐럿 */
  const caretIcon = isMobileLandscape
    ? { open: 'ph:caret-right', close: 'ph:caret-left' }
    : { open: 'ph:caret-down', close: 'ph:caret-up' };

  const drawerVariants = isMobileLandscape
    ? { open: { width: 'auto' }, closed: { width: 0 } }
    : { open: { height: 'auto' }, closed: { height: 0 } };

  const initialMotion = isMobileLandscape ? { width: 0 } : { height: 0 };

  return (
    <>
      <Styled.DrawerWrapper
        className={dimmedClassName}
        isMobileLandscape={isMobileLandscape}
        bottomOffset={bottomOffset}
        gap={16}
        justifyContent="flex-end"
        onClickCapture={handleGuardClickCapture}
        onClick={stopClickPropagation}
      >
        <Stack
          gap={isDrawerOpen ? 8 : 0}
          direction={isMobileLandscape ? 'row' : 'column'}
        >
          <Styled.DrawerToggleButton
            className={cn({
              backgroundOpacity: isMobileLandscape && !isDrawerOpen,
            })}
            variant="zinc_300"
            radius={9999}
            leftIcon={
              <Icon
                icon={
                  (isDrawerOpen ? caretIcon.open : caretIcon.close) as IconNames
                }
                size={19}
                color="black"
              />
            }
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setIsDrawerOpen((prev) => !prev);
            }}
            innerStyle={{
              backgroundColor:
                isMobileLandscape && !isDrawerOpen ? 'transparent' : '',
            }}
          />
          <Styled.MotionDrawer
            isFlexRow={isMobileLandscape}
            initial={initialMotion}
            animate={isDrawerOpen ? drawerVariants.open : drawerVariants.closed}
            transition={{ duration: 0.15 }}
          >
            {!isDisableHelpers && isDrawerOpen && (
              <>
                <MobilePdfDownloadButton
                  fileName={document.name}
                  isBulk={isBulk}
                  isDownloadEnabled={document.isDownloadEnabled}
                  ctaFormPage={ctaFormPage}
                  translateKoToEn={translateKoToEn}
                  isRedirectCtaFormAppropriate={isRedirectCtaFormAppropriate}
                />
                {document.isShareable && (
                  <MobileShareButton
                    domain={domain}
                    fileName={document.name}
                    translateKoToEn={translateKoToEn}
                    isBulk={isBulk}
                  />
                )}
                <Styled.ZoomPillButton
                  className={cn({ isLandscape: isMobileLandscape })}
                  variant="zinc_500"
                  radius={9999}
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Stack
                    direction={isMobileLandscape ? 'row' : 'column'}
                    gap={16}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Styled.ZoomIcon
                      icon="ph:magnifying-glass-plus"
                      size={19}
                      color={zoom >= V2_ZOOM_MAX ? 'zinc_400' : 'black'}
                      onClick={handleClickZoomIn}
                    />
                    <Styled.ZoomIcon
                      icon="ph:magnifying-glass-minus"
                      size={19}
                      color={zoom <= V2_ZOOM_MIN ? 'zinc_400' : 'black'}
                      onClick={handleClickZoomOut}
                    />
                  </Stack>
                </Styled.ZoomPillButton>
                <Styled.RoundIconButton
                  variant="zinc_500"
                  radius={9999}
                  aria-label={
                    isFullscreenActive
                      ? translateKoToEn('exit-fullscreen')
                      : translateKoToEn('fullscreen')
                  }
                  leftIcon={
                    <Icon
                      icon={
                        isFullscreenActive ? 'ph:arrows-in' : 'ph:arrows-out'
                      }
                      size={19}
                      color="black"
                    />
                  }
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    toggleFullscreen();
                    setIsDrawerOpen(false);
                  }}
                />
                <MobileLanguageButton />
                {/*{!isPreview && (*/}
                {/*  <Styled.RoundIconButton*/}
                {/*    variant="zinc_500"*/}
                {/*    radius={9999}*/}
                {/*    aria-label={translateKoToEn('viewer-switch-back')}*/}
                {/*    leftIcon={*/}
                {/*      <Icon icon="ph:arrow-u-up-left" size={19} color="black" />*/}
                {/*    }*/}
                {/*    onClick={(e: MouseEvent<HTMLButtonElement>) => {*/}
                {/*      e.stopPropagation();*/}
                {/*      setVersion('v1');*/}
                {/*    }}*/}
                {/*  />*/}
                {/*)}*/}
              </>
            )}
          </Styled.MotionDrawer>
        </Stack>
      </Styled.DrawerWrapper>

      <Styled.NavPill
        className={dimmedClassName}
        bottomOffset={bottomOffset}
        onClickCapture={handleGuardClickCapture}
        onClick={stopClickPropagation}
      >
        <Styled.NavArrowButton
          type="button"
          aria-label={translateKoToEn('prev-page')}
          disabled={currentPage <= 1}
          onClick={(e) => {
            e.stopPropagation();
            goToPrevPage();
          }}
        >
          <Icon icon="ph:caret-left" size={24} color="black" />
        </Styled.NavArrowButton>

        <Styled.IndicatorWrapper alignItems="center" gap={8}>
          <div
            style={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Icon
              icon="ph:list-dashes"
              size={19}
              color={isThumbnailPanelOpen ? 'skyblue_500' : 'black'}
              onClick={(e) => {
                e.stopPropagation();
                setIsThumbnailPanelOpen((prev) => !prev);
              }}
            />
          </div>
          <Styled.CurrentPageBox alignContent="center" justifyContent="center">
            <Text variant="sm-medium" color="black" lineHeight="15px">
              {currentPage}
            </Text>
          </Styled.CurrentPageBox>
          <Text
            variant="sm-medium"
            color="black"
            style={{
              width: 'max-content',
            }}
          >
            / {document.totalPages}
          </Text>
        </Styled.IndicatorWrapper>

        <Styled.NavArrowButton
          type="button"
          aria-label={translateKoToEn('next-page')}
          disabled={currentPage >= document.totalPages || isNextPageDisabled}
          onClick={(e) => {
            e.stopPropagation();
            goToNextPage();
          }}
        >
          <Icon icon="ph:caret-right" size={24} color="black" />
        </Styled.NavArrowButton>
      </Styled.NavPill>
    </>
  );
};

export default MobileBottomBar;
